// Who is signed in, and what their role allows.
//
// The answers come from shared/permissions.js, the same map the server builds
// its route guards from. This is convenience for rendering only. Every rule is
// also enforced on the server, which is what actually protects the data.
// Section 9 is explicit that hiding a control is not authorization.

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { getMe, logout as apiLogout } from './api.js'
import { can, canView, navFor, homePathFor } from '../../shared/permissions.js'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [allowedDomain, setAllowedDomain] = useState('fastestlabs.com')
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const data = await getMe()
      setUser(data?.user ?? null)
      if (data?.allowedDomain) setAllowedDomain(data.allowedDomain)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const signOut = useCallback(async () => {
    // Sign out locally whatever the server says; a failed logout call must
    // not keep someone signed in on a shared machine.
    await apiLogout().catch(() => {})
    setUser(null)
    window.location.assign('/signin')
  }, [])

  const role = user?.role

  const value = {
    user,
    allowedDomain,
    loading,
    refresh,
    signOut,
    // Ask what this person may do, not who they are. A new role then works
    // everywhere without a single component changing.
    can: (action) => can(role, action),
    canView: (view) => canView(role, view),
    nav: navFor(role),
    homePath: homePathFor(role),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return context
}
